import { Component, DestroyRef, EventEmitter, inject, Inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { IWebhookIntegration } from '@services/integrations.service';
import { NeuronsensorsService, nsISystem, nsWebhookConfig } from '@services/neuronsensors.service';
import { distinctUntilChanged, filter, finalize, firstValueFrom, map, Subject, switchMap, take, takeUntil } from 'rxjs';
import { generateGUID } from 'src/app/functions/functions'
import { Clipboard } from '@angular/cdk/clipboard';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { combineLatest } from 'rxjs';
import { Account, IAccount } from '@services/accounts.service';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';
import { Organisation } from '@services/organisation-data.service';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'app-edit-webhook-integration-neuronsensor',
	templateUrl: './edit-webhook-integration-neuronsensor.component.html',
	styleUrls: ['./edit-webhook-integration-neuronsensor.component.scss']
})
export class EditWebhookIntegrationNeuronSensorComponent implements OnInit, OnDestroy {

	private destroyRef = inject(DestroyRef);

	/** Emits when image updates occur from child actions */
	@Output() imageUpdated = new EventEmitter<any>();

	/** Indicates whether the integration is a Neuron Sensor webhook */
	public isNeuronSensor: boolean = false;

	/** List of available integrations retrieved from API */
	public IntegrationList: nsWebhookConfig[];

	public selectedIntegration: nsWebhookConfig;

	/** Reactive form backing this dialog */
	dialogForm: FormGroup;

	/** Currently selected account object */
	public selectedAccount: IAccount;

	/** GUID of currently selected account */
	public selectedAccountGUID: string

	/** Currently selected organisation */
	public selectedOrganisation: Organisation;

	/** Legacy manual teardown subject (kept for compatibility paths) */
	private finalise = new Subject<void>();

	/** Dropdown field mapping configuration */
	public fields: Object = { text: 'name', id: 'id' };

	/** Dropdown placeholder text */
	public waterMark: string = 'Select Integration';

	/** Required HTTP headers for webhook validation */
	public requiredHeaders = ['Content-Type', 'AccountUID', 'OrganisationUID', 'ClientWebhookID'];

	/** Selected webhook ID value */
	public selectedId: string

	/** Flag indicating whether systems have been successfully retrieved */
	public SystemsRetrieved: boolean

	/** Flag indicating whether a webhook was found in the list */
	public WebhookFound: boolean

	public nsWebhookNameValid: boolean = false;

	public headersOK: boolean

	/** Syncfusion dropdown reference for webhook selection */
	@ViewChild('WebhookDropdown') WebhookDropdown: DropDownList;

	/** Material toggle reference for enabling/disabling webhook */
	@ViewChild('switchWebhookEnabled') switchWebhookEnabled: MatSlideToggle;

	public ElWatchSystems: nsISystem[];
	public SelectedSystem: nsISystem;
	public myAccounts: IAccount[];
	public myOrganisations: Organisation[];

	/**
	 * Component constructor
	 * @param globalFunctions Shared UI/utility service
	 * @param fb Reactive form builder
	 * @param dialogRef Material dialog reference
	 * @param dialogData Injected dialog payload (integration model)
	 * @param neuronsensorsService API service for NeuronSensor operations
	 * @param clipboard Clipboard service for copy operations
	 * @param siteSubscriptions Global state subscription service
	 */
	constructor(
		private globalFunctions: GlobalFunctions,
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<EditWebhookIntegrationNeuronSensorComponent>,
		@Inject(MAT_DIALOG_DATA) public dialogData: IWebhookIntegration,
		private neuronsensorsService: NeuronsensorsService,
		private clipboard: Clipboard,
		private siteSubscriptions: siteSubscriptions,
	) {

		// Initialise reactive form using injected dialog data
		this.dialogForm = this.fb.group({
			OrganisationWebhookID: [this.dialogData.OrganisationWebhookID || 0],
			OrganisationID: [this.dialogData.OrganisationID || 0],
			OrganisationUID: [this.dialogData.OrganisationUID || ""],
			WebhookID: [this.dialogData.WebhookID || 0],
			WebhookUID: [this.dialogData.WebhookUID || generateGUID()],
			ClientWebhookID: [this.dialogData.ClientWebhookID || '', Validators.required],
			Host: [this.dialogData.Host || ''],
			Path: [this.dialogData.Path || ''],
			FQN: [this.dialogData.FQN || ''],
			Description: [this.dialogData.Description || ''],
			AutoBuild: [this.dialogData.AutoBuild],
			Enabled: [this.dialogData.Enabled],
			WebhookTypeID: [this.dialogData.WebhookTypeID || 0],
			Type: [this.dialogData.Type || 0, Validators.required],
			TypePath: [this.dialogData.TypePath || ""],
			TypeDescription: [this.dialogData.TypeDescription || ''],
			TypeAzureCode: [this.dialogData.TypeAzureCode || ""],
			Deleted: [this.dialogData.Deleted],
			NeuronSensorAPI: [this.dialogData.NeuronSensorAPI],
			NeuronSensorSystem: [this.dialogData.NeuronSensorSystem],
			NeuronSensors: [this.dialogData.NeuronSensors],
			NeuronSensorsWebhookEnabled: [false]
		});

		// Construct fully qualified endpoint once from initial data
		const fqn = `${this.dialogData.Host}${this.dialogData.Path}?code=${this.dialogData.TypeAzureCode}`;
		this.dialogForm.get('FQN')?.setValue(fqn);

		// Combine account + organisation state streams to ensure UI is fully initialised
		combineLatest([
			this.siteSubscriptions.myAccounts$.pipe(takeUntilDestroyed(this.destroyRef)),
			this.siteSubscriptions.selectedAccount$.pipe(takeUntilDestroyed(this.destroyRef)),
			this.siteSubscriptions.myOrganisations$.pipe(takeUntilDestroyed(this.destroyRef)),
			this.siteSubscriptions.selectedOrganisation$.pipe(takeUntilDestroyed(this.destroyRef)),
		]).pipe(
			filter(([a, b, c, d]) => !!a && !!b && !!c && !!d)
		).subscribe(([myAccounts, account, myOrganisations, organisation]) => {

			this.myAccounts = this.siteSubscriptions.normalizeFeatures(myAccounts as Account[]) as Account[];
			this.myOrganisations = myOrganisations;

			this.selectedAccount = this.siteSubscriptions.normalizeFeatures(account as Account) as Account;
			this.selectedAccountGUID = this.selectedAccount.GUID;
			this.selectedOrganisation = organisation;

			// Trigger async initialisation after core state is ready
			void this.initNeuronSensorFlow();
		});

		// React to API key changes -> fetch available systems
		this.dialogForm.get('NeuronSensorAPI')!.valueChanges.pipe(
			filter(Boolean),
			distinctUntilChanged(),
			takeUntilDestroyed(this.destroyRef),
			switchMap(apiKey =>
				this.neuronsensorsService.getSystems$(apiKey).pipe(
					map(systems => ({ apiKey, systems }))
				)
			)
		).subscribe(({ apiKey, systems }) => {

			this.ElWatchSystems = systems as nsISystem[];

			if (!this.ElWatchSystems.length) {
				this.SystemsRetrieved = false;
				this.resetIntegrationState();
				return;
			}

			this.SystemsRetrieved = true;

			this.SelectedSystem = <nsISystem>this.ElWatchSystems[0];

			const systemID = this.SelectedSystem.id;
			const systemDesc = this.SelectedSystem.description;

			this.dialogForm.get('Description')?.setValue(systemDesc, { emitEvent: false });
			this.dialogForm.get('NeuronSensorSystem')?.setValue(systemID, { emitEvent: false });

			this.loadWebhooks(apiKey, systemID);
		});

		// React to system selection changes -> reload webhooks
		this.dialogForm.get('NeuronSensorSystem')!.valueChanges.pipe(
			filter(Boolean),
			distinctUntilChanged(),
			takeUntilDestroyed(this.destroyRef)
		).subscribe(systemID => {
			const apiKey = this.dialogForm.get('NeuronSensorAPI')?.value;
			this.loadWebhooks(apiKey, systemID);
		});

		// React to webhook selection changes -> update selected integration state
		this.dialogForm.get('ClientWebhookID')?.valueChanges.pipe(
			filter(Boolean),
			distinctUntilChanged(),
			takeUntilDestroyed(this.destroyRef)
		).subscribe(id => {
			this.selectedIntegration = this.IntegrationList.find(i => i.id === id) as nsWebhookConfig;

			if (this.selectedIntegration) {
				this.nsWebhookNameValid =
					this.selectedIntegration.name.includes(this.dialogData.WebhookUID);
			}
		});

		// Enforce required validators for API and system selection
		this.dialogForm.get('NeuronSensorAPI')?.addValidators(Validators.required);
		this.dialogForm.get('NeuronSensorSystem')?.addValidators(Validators.required);

		// Store initial selected webhook ID
		this.selectedId = this.dialogForm.get('ClientWebhookID')?.value;
	}

	/**
	 * Load webhooks for selected system
	 * @param apiKey API authentication key
	 * @param systemID Selected NeuronSensor system identifier
	 */
	private loadWebhooks(apiKey: string, systemID: string): void {
		if (!apiKey || !systemID) return;
		void this.getWebhooks(apiKey, systemID, this.selectedId);
	}

	/**
	 * Initialise full workflow once account + system context is ready
	 */
	private async initNeuronSensorFlow(): Promise<void> {
		const apiKey = this.dialogForm.get('NeuronSensorAPI')?.value;
		if (!apiKey) return;

		const systems = await this.getSystems(apiKey);

		if (!systems?.length) {
			this.SystemsRetrieved = false;
			this.resetIntegrationState();
			return;
		}

		this.SystemsRetrieved = true;

		const systemID = systems[0].id;

		this.SelectedSystem = systems[0];

		this.dialogForm.get('NeuronSensorSystem')?.setValue(systemID);

		await this.getWebhooks(apiKey, systemID, this.selectedId);
	}

	/**
	 * Cleanup lifecycle hook
	 */
	ngOnDestroy(): void {
		this.finalise.next();
		this.finalise.complete();
	}

	/**
	 * Reset integration-related UI state
	 */
	private resetIntegrationState(): void {
		this.IntegrationList = [];
		this.selectedIntegration = {} as nsWebhookConfig;
		this.dialogForm.get('NeuronSensorSystem')?.setValue("");
		this.dialogForm.get('ClientWebhookID')?.setValue("");
		this.dialogForm.get('Description')?.setValue("");
		this.WebhookDropdown.value = "";
		this.dialogForm.markAsPristine();
	}

	/**
	 * Get account display name
	 * @param accountUID Account GUID to resolve display name
	 */
	getAccountName(accountUID: string): string {
		var account = this.myAccounts.find(acc => acc.GUID == accountUID)
		if (account) {
			return account.Name
		} else { return "" }
	}

	/**
	 * Get organisation display name
	 * @param OrganisationUID Organisation GUID to resolve display name
	 */
	getOrganisationName(OrganisationUID: string): string {
		var organisation = this.myOrganisations.find(org => org.Uid == OrganisationUID)
		if (organisation) {
			return organisation.Name
		} else { return "" }
	}

	/** Update webhook name and validate uniqueness before patching */
	updateWebhookName(integration: nsWebhookConfig) {

		if (this.IntegrationList.find(i => i.name.includes(this.dialogData.WebhookUID))) {
			this.globalFunctions.UserConfirm$(`This name already exists, Please select correct webhook from the list`, "Name Exists", "OK", null)
				.subscribe(OKResult => { });
			return;
		}

		var name = `PRISMPdM-${this.dialogData.WebhookUID}`

		this.globalFunctions.UserConfirm$(`Update Webhook Name => ${name}?`, "Update Name", "Yes", "No")
			.subscribe(confirmed => {
				if (confirmed) {
					var _editIntegration = { ...integration }

					if (!this.IntegrationList.find(i => i.name.includes(this.dialogData.WebhookUID))) {
						_editIntegration.name = name
					} else {
						this.globalFunctions.UserConfirm$(`This name already exists, Please select correct webhook from the list`, "Name Exists", "OK", null)
							.subscribe(confirmed => { });
						return;
					}

					this.patchWebhook(_editIntegration);
				}
			});
	}

	/** Update webhook configuration and persist changes */
	updateWebhookDetails(integration: nsWebhookConfig) {

		this.globalFunctions.UserConfirm$(`Update Webhook Configuration`, "Update Neuronsensors", "Yes", "No")
			.subscribe(confirmed => {
				if (confirmed) {

					var _editIntegration = { ...integration }

					_editIntegration.headers["Content-Type"] = "application/json"
					_editIntegration.headers["AccountUID"] = this.selectedAccount.GUID
					_editIntegration.headers["OrganisationUID"] = this.dialogForm.get('OrganisationUID')?.value
					_editIntegration.headers["ClientWebhookID"] = _editIntegration.id

					var FQN = `${this.dialogData.Host}${this.dialogData.Path}?code=${this.dialogData.TypeAzureCode}`
					_editIntegration.endpoint = FQN

					this.dialogForm.get('FQN')?.setValue(FQN)

					this.patchWebhook(_editIntegration);
				}
			});
	}

	/** Create a new webhook integration */
	addWebhook() {

		if (!this.IntegrationList.find(i => i.name.includes(this.dialogData.WebhookUID))) {

			var name = `PRISMPdM-${this.dialogForm.get('WebhookUID')?.value}`;

			var _editIntegration = {} as nsWebhookConfig

			this.globalFunctions.UserConfirm$(`Create new webhook in ${this.SelectedSystem.name}`)
				.subscribe(confirmed => {

					if (confirmed) {

						_editIntegration.type = "POST";
						_editIntegration.system = this.dialogForm.get('NeuronSensorSystem')?.value;

						_editIntegration.body = {};
						_editIntegration.disabled = 100;
						_editIntegration.name = name;
						_editIntegration.sensors = [];

						_editIntegration.headers = {};
						_editIntegration.headers["Content-Type"] = "application/json";
						_editIntegration.headers["AccountUID"] = this.selectedAccount.GUID;
						_editIntegration.headers["OrganisationUID"] = this.selectedOrganisation.Uid;

						var FQN = `${this.dialogData.Host}${this.dialogData.Path}?code=${this.dialogData.TypeAzureCode}`
						_editIntegration.endpoint = FQN

						this.postWebhook(_editIntegration);
					}
				});
		} else {
			this.globalFunctions.UserConfirm$(`A webhook with this Integration ID already exists`).subscribe(confirmed => { });
		}
	}

	/**
	 * Patch existing webhook integration
	 * @param _edit Webhook configuration to update
	 */
	async patchWebhook(_edit: nsWebhookConfig): Promise<void> {
		const apiKey = this.dialogForm.get('NeuronSensorAPI')?.value;
		const systemID = this.dialogForm.get('NeuronSensorSystem')?.value;

		try {
			const success = await firstValueFrom(
				this.neuronsensorsService.patchIntegration$(apiKey, systemID, _edit)
			);

			if (!success) return;

			if (this.selectedIntegration) {
				Object.assign(this.selectedIntegration, _edit);

				this.nsWebhookNameValid =
					this.selectedIntegration.name.includes(this.dialogData.WebhookUID);
			}

			await this.getWebhooks(apiKey, systemID, _edit.id);
		}
		catch (error) {
			this.globalFunctions.HandleError(
				error,
				"Error: Error patching Webhook",
				true
			);
		}
	}

	/**
	 * Create new webhook integration
	 * @param _new Webhook configuration to create
	 */
	async postWebhook(_new: nsWebhookConfig): Promise<void> {
		const apiKey = this.dialogForm.get('NeuronSensorAPI')?.value;
		const systemID = this.dialogForm.get('NeuronSensorSystem')?.value;

		try {
			const success = await firstValueFrom(
				this.neuronsensorsService.postIntegration$(apiKey, systemID, _new)
			);

			if (!success) return;

			const created = success.data as nsWebhookConfig;

			this.updateWebhookDetails(created);
		}
		catch (error) {
			this.globalFunctions.HandleError(
				error,
				"Error: Error patching Webhook",
				true
			);
		}
	}

	/**
	 * Validate whether a required header exists and matches expected values
	 * @param key Header key to validate
	 */
	getHeaderExists(key: string): boolean {

		var OK: boolean = false;
		let headers = this.selectedIntegration.headers

		if (!headers[key]) {
			this.headersOK = false;
			return false;
		}

		switch (key) {
			case 'Content-Type':
				OK = headers[key] === 'application/json';
				break;
			case 'AccountUID':
				OK = headers[key] === this.selectedAccount.GUID;
				break;
			case 'OrganisationUID':
				OK = headers[key] === this.dialogForm.get('OrganisationUID')?.value;
				break;
			case 'ClientWebhookID':
				OK = headers[key] === this.selectedIntegration.id;
				break;
			default:
				OK = false;
				break;
		}

		if (!OK) {
			this.headersOK = false;
		}

		return OK
	}

	/**
	 * Handle dropdown selection change
	 * @param event Dropdown selection event containing selected webhook item
	 */
	onDropdownChange(event: any) {
		const selected = <nsWebhookConfig>event.itemData
		this.headersOK = true;

		if (selected) {
			const currentValue = this.dialogForm.get('ClientWebhookID')?.value;

			if (currentValue !== selected.id) {
				this.dialogForm.get('ClientWebhookID')?.setValue(selected.id);
				this.dialogForm.get('ClientWebhookID')?.markAsDirty();
			}
		}
	}

	/**
	 * Initialise component lifecycle
	 */
	ngOnInit(): void {
		this.dialogForm.markAsPristine();
	}

	/**
	 * Retrieve systems for a given API key
	 * @param apiKey NeuronSensor API key used to fetch systems
	 */
	private async getSystems(apiKey: string): Promise<nsISystem[]> {
		const systems = await firstValueFrom(
			this.neuronsensorsService.getSystems$(apiKey)
		);

		this.ElWatchSystems = systems;

		return systems;
	}

	/**
	 * Toggle webhook enabled/disabled state
	 * @param event Mouse event triggering toggle interaction
	 */
	nsEnable(event: MouseEvent) {

		event.preventDefault();
		event.stopPropagation();

		const enabled = this.selectedIntegration.disabled < 100;

		this.globalFunctions.UserConfirm$(`${enabled ? "Disable" : "Enable"} this webhook on NeuronSensors?`)
			.subscribe(confirmed => {
				if (confirmed) {

					this.selectedIntegration.disabled = enabled ? 100 : 0;

					this.switchWebhookEnabled.checked = !enabled;

					this.patchWebhook({ ...this.selectedIntegration });
				}
			});
	}

	/**
	 * Retrieve webhook integrations for a system
	 * @param apiKey API authentication key
	 * @param systemID System identifier
	 * @param webhookID Optional webhook ID to preselect
	 */
	async getWebhooks(apiKey: string, systemID: string, webhookID: string = ""): Promise<void> {

		if (apiKey === "" || systemID === "") {
			this.IntegrationList = [];
			return;
		}

		try {
			this.IntegrationList = await firstValueFrom(
				this.neuronsensorsService.getIntegrationByType$(apiKey, systemID, ["POST"])
			) as nsWebhookConfig[];

			webhookID = webhookID || this.dialogForm.get('ClientWebhookID')?.value;

			this.WebhookFound = false;

			if (webhookID) {

				this.selectedIntegration = this.IntegrationList.find(i => i.id === webhookID) as nsWebhookConfig;

				if (this.selectedIntegration) {
					this.nsWebhookNameValid = this.selectedIntegration.name.includes(this.dialogData.WebhookUID);
					this.WebhookDropdown.value = this.selectedIntegration.name;
				}

				if (!this.IntegrationList.some(i => i.name.includes(this.dialogData.WebhookUID))) {
					this.WebhookFound = true;
				}
			}
		}
		catch (error) {
			this.IntegrationList = [];
			this.WebhookFound = false;
		}
	}

	/**
	 * Handle dialog cancel action with unsaved-change protection
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * Copy arbitrary string value to clipboard
	 * @param value Text to copy to clipboard
	 */
	copyToClipboard(value: string) {
		try {
			if (this.clipboard.copy(value)) {
				this.globalFunctions.DisplaySnackBar({
					message: "Copied to clipboard",
					action: "",
					duration: 2000
				});
			};
		} catch (error) { console.error(error); }
	}

	/**
	 * Copy fully qualified endpoint (FQN) to clipboard
	 */
	copyFQNToClipboard() {
		try {
			const value = `${this.dialogData.Host}${this.dialogData.Path}?code=${this.dialogData.TypeAzureCode}`;
			if (this.clipboard.copy(value)) {
				this.globalFunctions.DisplaySnackBar({
					message: "Copied to clipboard",
					action: "",
					duration: 2000
				});
			};
		} catch (error) { console.error(error); }
	}
}